import type { BackgroundDefinition, BackgroundId } from '@/engine/types'
import { spriteAssetUrls } from '@/content/sprites'
import {
  resolveManagedVisual,
  runtimeManagedVisualUrls,
  v18BackgroundVisuals,
} from '@/content/visualAssets'
import { districtArchiveAssetUrls } from '@/content/districtArchive'

// 未生成的章节背景先提供确定性的渐变降级；最终素材到位后只需补充 src，不改剧情节点。
export const backgrounds: Record<BackgroundId, BackgroundDefinition> = {
  invitation: {
    id: 'invitation',
    src: '/assets/cg/invitation-phone-v1.webp',
    collectionId: 'cg00-invitation',
    alt: '夏夜雨声中的房间里，手机屏幕亮起一条老玩家发来的新区邀请',
    focalPoint: '58% center',
    gradient: 'linear-gradient(135deg, #15143a, #3b1f3e 52%, #9c633d)',
  },
  login: {
    id: 'login',
    src: '/assets/backgrounds/galgame/login-v1.webp',
    collectionId: 'cg12-launch-login',
    alt: '4945区开服登录界面与尚未稳定的排行榜',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 35%, #344a8f, #11152f 54%, #070916)',
  },
  recruitment: {
    id: 'recruitment',
    src: '/assets/backgrounds/galgame/recruitment-v1.webp',
    collectionId: 'cg13-recruitment-square',
    alt: '世界频道里多条组织招募信息迅速上滚',
    focalPoint: 'center center',
    gradient: 'linear-gradient(145deg, #182a58, #26133f 55%, #080b1c)',
  },
  organization: {
    id: 'organization',
    src: '/assets/backgrounds/galgame/organization-hall-v1.webp',
    collectionId: 'cg11-organization-hall',
    alt: '新区组织大厅，五个组织席位依次亮起',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 72%, #553e82, #121834 48%, #060812)',
  },
  mutedKey: {
    id: 'mutedKey',
    src: '/assets/backgrounds/galgame/muted-key-v1.webp',
    collectionId: 'cg01-muted-key',
    alt: '辰逸因未得到回应而按下管理员禁言，takemehand尚未意识到群聊权限已被动用',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 33% 64%, #b775c9, #17183f 45%, #060814)',
  },
  worldChat: {
    id: 'worldChat',
    src: '/assets/backgrounds/galgame/world-chat-v1.webp',
    collectionId: 'cg02-world-chat',
    alt: '世界频道的消息像弹幕一样交错，多个组织同时发言',
    focalPoint: 'center center',
    gradient: 'conic-gradient(from 220deg at 50% 55%, #111b43, #3c1d54, #142c52, #111b43)',
  },
  twoGroups: {
    id: 'twoGroups',
    src: '/assets/backgrounds/galgame/two-groups-v1.webp',
    collectionId: 'cg03-two-groups',
    alt: '两个都自称4945官方群的聊天窗口彼此重叠',
    focalPoint: 'center center',
    gradient: 'linear-gradient(110deg, #182a58 0 48%, #070916 48% 52%, #542647 52%)',
  },
  warRoom: {
    id: 'warRoom',
    src: '/assets/backgrounds/galgame/war-room-v1.webp',
    collectionId: 'cg04-war-room',
    alt: '深夜作战桌上铺着出勤表、截图和要塞目标',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 65%, #714d42, #17213f 45%, #070916)',
  },
  fortress: {
    id: 'fortress',
    src: '/assets/backgrounds/galgame/fortress-v1.webp',
    collectionId: 'cg05-fortress',
    alt: '火光和蓝紫弹幕轨迹交错的要塞战场',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 52% 47%, #bf593f, #38254d 38%, #091229 73%)',
  },
  aftermath: {
    id: 'aftermath',
    src: '/assets/backgrounds/galgame/aftermath-v1.webp',
    collectionId: 'cg08-aftermath',
    alt: '要塞战后的空场与仍在闪烁的成员列表',
    gradient: 'linear-gradient(180deg, #3e3d68, #171b38 48%, #080a17)',
  },
  rewardHall: {
    id: 'rewardHall',
    src: '/assets/backgrounds/galgame/reward-hall-v1.webp',
    collectionId: 'cg06-reward-hall',
    alt: '稀有饰品分配记录悬在组织大厅中央',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 43%, #96715a, #292344 38%, #080a18 72%)',
  },
  sixthSeat: {
    id: 'sixthSeat',
    src: '/assets/backgrounds/galgame/sixth-seat-v1.webp',
    collectionId: 'cg07-sixth-seat',
    alt: '五个明亮席位之外，第六个席位刚刚点亮',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 72% 62%, #2a8b76, #172343 38%, #070916 75%)',
  },
  nightMessage: {
    id: 'nightMessage',
    src: '/assets/backgrounds/galgame/night-message-v1.webp',
    collectionId: 'cg09-night-message',
    alt: '深夜聊天界面亮起，组织冲突相关的私聊与离线消息等待处理',
    gradient: 'linear-gradient(155deg, #182342, #12142b 52%, #090a14)',
  },
  romanceGate: {
    id: 'romanceGate',
    src: '/assets/backgrounds/galgame/romance-gate-v1.webp',
    collectionId: 'cg50-romance-gate',
    alt: '幻想夜市入口的东方月门在深蓝夜色与暖色灯笼间敞开',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #6c3850, #151a38 52%, #080914)',
  },
  romanceFoodStreet: {
    id: 'romanceFoodStreet',
    src: '/assets/backgrounds/galgame/romance-food-street-v1.webp',
    collectionId: 'cg51-romance-food-street',
    alt: '灯火与幽灵火苗交错的妖怪食街，摊位在夜色中等待来客',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #8c4b50, #291b3b 46%, #090b1b)',
  },
  romanceArcade: {
    id: 'romanceArcade',
    src: '/assets/backgrounds/galgame/romance-arcade-v1.webp',
    collectionId: 'cg52-romance-arcade',
    alt: '挂满风铃、符卡与练习靶的夜市游艺场',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 52%, #755053, #18244c 48%, #080b1c)',
  },
  romanceLanternBridge: {
    id: 'romanceLanternBridge',
    src: '/assets/backgrounds/galgame/romance-lantern-bridge-v1.webp',
    collectionId: 'cg53-romance-lantern-bridge',
    alt: '满月下的拱桥横跨河面，花灯沿水流向远处',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 51% 26%, #4868a1, #14203d 48%, #070a14)',
  },
  romanceGateRain: {
    id: 'romanceGateRain',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceGateRain),
    alt: '细雨中的幻想夜市月门，灯笼倒影落在湿润石路上',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #494c72, #121a32 52%, #070913)',
    placeholderFor: v18BackgroundVisuals.romanceGateRain.final,
    promptRef: v18BackgroundVisuals.romanceGateRain.promptRef,
  },
  romanceGateAfterHours: {
    id: 'romanceGateAfterHours',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceGateAfterHours),
    alt: '打烊后的幻想夜市月门，只剩少量引路灯和安静街巷',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #44324e, #11172c 52%, #060711)',
    placeholderFor: v18BackgroundVisuals.romanceGateAfterHours.final,
    promptRef: v18BackgroundVisuals.romanceGateAfterHours.promptRef,
  },
  romanceFoodStreetRain: {
    id: 'romanceFoodStreetRain',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceFoodStreetRain),
    alt: '雨夜的妖怪食街，暖色摊灯映在伞面和湿石板上',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #76545e, #202039 48%, #080a18)',
    placeholderFor: v18BackgroundVisuals.romanceFoodStreetRain.final,
    promptRef: v18BackgroundVisuals.romanceFoodStreetRain.promptRef,
  },
  romanceFoodStreetAfterHours: {
    id: 'romanceFoodStreetAfterHours',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceFoodStreetAfterHours),
    alt: '打烊后的妖怪食街，收起的摊位间只留两盏夜宵灯',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 48%, #623d49, #21192f 48%, #070814)',
    placeholderFor: v18BackgroundVisuals.romanceFoodStreetAfterHours.final,
    promptRef: v18BackgroundVisuals.romanceFoodStreetAfterHours.promptRef,
  },
  romanceArcadeRain: {
    id: 'romanceArcadeRain',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceArcadeRain),
    alt: '雨帘包围的夜市游艺场，符卡靶与风铃在檐下发亮',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 52%, #4e5971, #17203e 48%, #070918)',
    placeholderFor: v18BackgroundVisuals.romanceArcadeRain.final,
    promptRef: v18BackgroundVisuals.romanceArcadeRain.promptRef,
  },
  romanceArcadeAfterHours: {
    id: 'romanceArcadeAfterHours',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceArcadeAfterHours),
    alt: '闭馆后的夜市游艺场，收起的符卡与一排低亮风铃留下安静赛道',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 50% 52%, #554353, #151b35 48%, #060814)',
    placeholderFor: v18BackgroundVisuals.romanceArcadeAfterHours.final,
    promptRef: v18BackgroundVisuals.romanceArcadeAfterHours.promptRef,
  },
  romanceLanternBridgeRain: {
    id: 'romanceLanternBridgeRain',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceLanternBridgeRain),
    alt: '细雨覆盖的花灯拱桥，河面将月色与灯火拉成长线',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 51% 26%, #3d527d, #111a32 48%, #060812)',
    placeholderFor: v18BackgroundVisuals.romanceLanternBridgeRain.final,
    promptRef: v18BackgroundVisuals.romanceLanternBridgeRain.promptRef,
  },
  romanceLanternBridgeAfterHours: {
    id: 'romanceLanternBridgeAfterHours',
    src: resolveManagedVisual(v18BackgroundVisuals.romanceLanternBridgeAfterHours),
    alt: '打烊后的花灯拱桥，远处店铺熄灯，只剩河灯与月光',
    focalPoint: 'center center',
    gradient: 'radial-gradient(circle at 51% 26%, #35486f, #10172b 48%, #050710)',
    placeholderFor: v18BackgroundVisuals.romanceLanternBridgeAfterHours.final,
    promptRef: v18BackgroundVisuals.romanceLanternBridgeAfterHours.promptRef,
  },
  ending: {
    id: 'ending',
    src: '/assets/backgrounds/galgame/ending-dawn-v1.webp',
    collectionId: 'cg10-ending-dawn',
    alt: '黎明前的4945区仍然在线运行',
    gradient: 'linear-gradient(180deg, #2b3766, #432848 48%, #b06e57 100%)',
  },
}

// 构建与离线缓存共用这份完整资源清单，剧情新增 CG 必须在此显式登记。
export const projectAssetUrls = [
  ...Object.values(backgrounds).flatMap((background) => (background.src ? [background.src] : [])),
  ...spriteAssetUrls,
  ...runtimeManagedVisualUrls,
  ...districtArchiveAssetUrls,
  '/assets/ui/qq-group-1075594288-v1.webp',
  '/assets/cg/jiangnan-planning-v1.webp',
  '/assets/cg/title-shana-sunset-v1.webp',
  '/assets/cg/xilufei-group-explosion-v1.webp',
  '/assets/cg/xilufei-exit-v1.webp',
  '/assets/cg/bond-shana-v1.webp',
  '/assets/cg/bond-qifu-v1.webp',
  '/assets/cg/bond-heartbeat-v1.webp',
  '/assets/cg/bond-yanqiu-v1.webp',
  '/assets/cg/bond-wenxian-v1.webp',
  '/assets/cg/bond-takemehand-v1.webp',
  '/assets/cg/bond-xilufei-v1.webp',
  '/assets/cg/bond-swordheart-v1.webp',
  '/assets/cg/bond-huayue-v1.webp',
  '/assets/cg/r2-org6-remnants-v1.webp',
  '/assets/cg/bond-chenyi-v1.webp',
  '/assets/cg/bond-yyt-v1.webp',
  '/assets/cg/bond-avucii-v1.webp',
  '/assets/cg/pet-jiangjinjiu-v1.webp',
  '/assets/cg/route3-founding-v1.webp',
  '/assets/cg/route4-ledger-v1.webp',
  '/assets/cg/route5-empty-seat-v1.webp',
  '/assets/cg/route6-sixth-seat-v1.webp',
  '/assets/cg/route1-join-v1.webp',
  '/assets/cg/route1-fortress-v1.webp',
  '/assets/cg/world-enemy-v1.webp',
  '/assets/cg/council-reshuffle-v1.webp',
  '/assets/cg/second-fortress-v1.webp',
  '/assets/cg/rare-accessory-v1.webp',
  '/assets/cg/shi-joins-org2-v1.webp',
  '/assets/cg/chenyi-leaves-v1.webp',
  '/assets/cg/partial-transfer-v1.webp',
  '/assets/cg/ending-route2-v1.webp',
  '/assets/cg/ending-route3-v1.webp',
  '/assets/cg/ending-route4-v1.webp',
  '/assets/cg/ending-route5-v1.webp',
  '/assets/cg/ending-route6-v1.webp',
  '/assets/cg/yanqiu-handoff-v1.webp',
  '/assets/cg/yyt-avucii-handoff-v1.webp',
  '/assets/cg/season1-finale-v1.webp',
  // v1.7 first-date CGs stay literal so the build audit and offline cache can detect every missing deliverable.
  '/assets/cg/romance-shana-first-date-v1.webp',
  '/assets/cg/romance-qifu-first-date-v1.webp',
  '/assets/cg/romance-chenyi-first-date-v1.webp',
  '/assets/cg/romance-swordheart-first-date-v1.webp',
  '/assets/cg/romance-heartbeat-first-date-v1.webp',
  '/assets/cg/romance-yanqiu-first-date-v1.webp',
  '/assets/cg/romance-huayue-first-date-v1.webp',
  '/assets/cg/romance-wenxian-first-date-v1.webp',
  '/assets/cg/romance-takemehand-first-date-v1.webp',
  '/assets/cg/romance-xilufei-first-date-v1.webp',
  '/assets/cg/romance-yyt-first-date-v1.webp',
  '/assets/cg/romance-avucii-first-date-v1.webp',
  ...[
    'mentor', 'qifu', 'bottle', 'shana', 'chenyi', 'an', 'shi', 'daigu', 'xilufei', 'saoji',
    'swordheart', 'sixin', 'heartbeat', 'truth', 'oguri', 'yanqiu', 'huayue', 'xuanmo',
    'jiangjinjiu', 'takemehand', 'wenxian', 'gaobai', 'jianwen', 'yyt', 'emperor', 'avucii',
    'haogeju', 'lan', 'xingqing', 'suming', 'passerk', 'tiesuiya', 'kunxing', 'shen-zhixing', 'north-observer',
  ].map((id) => `/assets/avatars/${id}.webp`),
]
